import { useState, useRef, useCallback, useEffect } from 'react';
import { bufferToWave } from '../utils/audioHelpers';

interface RecorderState {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  mediaBlob: Blob | null; // WAV
  mediaUrl: string | null; // WAV
}

export const useAudioRecorder = () => {
  const [state, setState] = useState<RecorderState>({
    isRecording: false,
    isPaused: false,
    recordingTime: 0,
    mediaBlob: null,
    mediaUrl: null,
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const chunksRef = useRef<Float32Array[]>([]);
  const startTimeRef = useRef<number>(0);
  const timerIntervalRef = useRef<number | null>(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyserRef.current = analyser;

      // Create ScriptProcessorNode (bufferSize, inputChannels, outputChannels)
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      scriptProcessorRef.current = processor;

      chunksRef.current = [];

      processor.onaudioprocess = (e) => {
        if (state.isPaused) return;
        const inputData = e.inputBuffer.getChannelData(0);
        // Clone the data because the buffer is reused
        chunksRef.current.push(new Float32Array(inputData));
      };

      source.connect(analyser);
      analyser.connect(processor);
      processor.connect(audioContext.destination);

      startTimeRef.current = Date.now();
      setState((prev) => ({ 
        ...prev, 
        isRecording: true, 
        isPaused: false, 
        mediaBlob: null, 
        mediaUrl: null, 
        recordingTime: 0 
      }));

      timerIntervalRef.current = window.setInterval(() => {
        setState((prev) => ({ ...prev, recordingTime: prev.recordingTime + 1 }));
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  }, [state.isPaused]);

  const stopRecording = useCallback(() => {
    if (scriptProcessorRef.current && audioContextRef.current) {
      scriptProcessorRef.current.disconnect();
      analyserRef.current?.disconnect();
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Process audio data
      const audioContext = audioContextRef.current;
      const bufferLength = chunksRef.current.reduce((acc, chunk) => acc + chunk.length, 0);
      const audioBuffer = audioContext.createBuffer(1, bufferLength, audioContext.sampleRate);
      const channelData = audioBuffer.getChannelData(0);
      
      let offset = 0;
      for (const chunk of chunksRef.current) {
        channelData.set(chunk, offset);
        offset += chunk.length;
      }

      // Generate WAV
      const wavBlob = bufferToWave(audioBuffer, bufferLength);
      const wavUrl = URL.createObjectURL(wavBlob);

      setState((prev) => ({
        ...prev,
        isRecording: false,
        isPaused: false,
        mediaBlob: wavBlob,
        mediaUrl: wavUrl,
      }));

      // Cleanup
      audioContext.close();
      audioContextRef.current = null;
      scriptProcessorRef.current = null;
      analyserRef.current = null;
      mediaStreamRef.current = null;
      chunksRef.current = [];
    }
  }, []);

  const togglePause = useCallback(() => {
    if (state.isPaused) {
      // Resume
      audioContextRef.current?.resume();
      setState((prev) => ({ ...prev, isPaused: false }));
    } else {
      // Pause
      audioContextRef.current?.suspend();
      setState((prev) => ({ ...prev, isPaused: true }));
    }
  }, [state.isPaused]);

  const resetRecording = useCallback(() => {
    if (state.mediaUrl) URL.revokeObjectURL(state.mediaUrl);
    
    setState({
      isRecording: false,
      isPaused: false,
      recordingTime: 0,
      mediaBlob: null,
      mediaUrl: null,
      mp3Blob: null,
      mp3Url: null,
    });
  }, [state.mediaUrl, state.mp3Url]);

  // Visualizer data helper
  const getVisualizerData = useCallback(() => {
    if (!analyserRef.current) return new Uint8Array(0);
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    return dataArray;
  }, []);

  return {
    ...state,
    startRecording,
    stopRecording,
    togglePause,
    resetRecording,
    getVisualizerData,
  };
};
